import { createRoute, useNavigate } from '@tanstack/react-router'
import { useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, X } from 'lucide-react'
import { Button, Input } from '@arabquiz/ui'
import { Route as rootRoute } from '../__root'

/**
 * Reset-password route — Arabic, RTL.
 *
 * Consumes the link from the transactional email
 * (`…/auth/reset-password?userId=<guid>&token=<base64url>`), prompts the user
 * for a new password (twice), and POSTs to `/api/v1/auth/reset-password`. On
 * success we wait two seconds (long enough to read the confirmation) then
 * navigate to `/auth/sign-in`. On error we surface the failure and offer a
 * "request a new link" path back to `/auth/forgot-password`.
 *
 * If the URL is missing `userId` or `token`, we short-circuit to the error
 * state immediately so the user is not asked to type a password we cannot
 * use. Password rules mirror the API policy (8+ chars + complexity), but
 * the canonical enforcement happens server-side.
 */
export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/reset-password',
  validateSearch: (search: Record<string, unknown>) => ({
    userId: String(search['userId'] ?? ''),
    token: String(search['token'] ?? ''),
  }),
  component: ResetPasswordPage,
})

type ResetPasswordPayload = {
  newPassword: string
  confirmPassword: string
}

type ValidationKey = 'required' | 'invalidEmail' | 'passwordTooShort' | 'passwordMismatch'

const MIN_PASSWORD_LENGTH = 8

function ResetPasswordPage() {
  const { t } = useTranslation('auth')
  const { t: tValidation } = useTranslation('validation')
  const { t: tCommon } = useTranslation('common')
  const { userId, token } = Route.useSearch()
  const navigate = useNavigate()

  const resetMutation = useMutation({
    mutationFn: async (payload: ResetPasswordPayload) => {
      const res = await fetch('/api/v1/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          token,
          newPassword: payload.newPassword,
        }),
        credentials: 'include',
      })
      if (!res.ok) {
        const text = await res.text()
        throw new Error(text || `Reset failed: ${res.status}`)
      }
      return res.json() as Promise<{ message: string }>
    },
  })

  const form = useForm({
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    } as ResetPasswordPayload,
    onSubmit: async ({ value }) => {
      await resetMutation.mutateAsync(value)
    },
  })

  // After a successful reset, give the user two seconds to read the
  // confirmation and then route them to sign-in. Cleaned up on unmount so
  // strict-mode double-effects don't double-navigate.
  useEffect(() => {
    if (!resetMutation.isSuccess) return
    const timer = setTimeout(() => {
      void navigate({ to: '/auth/sign-in' })
    }, 2000)
    return () => clearTimeout(timer)
  }, [resetMutation.isSuccess, navigate])

  // No token => invalid link. Short-circuit before rendering the form.
  if (!userId || !token) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-gradient-to-bl from-brand-50/60 via-white to-brand-100/30 px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-xl border border-brand-100 p-8 sm:p-10 text-center">
            <div
              className="inline-block h-1 w-12 rounded-full bg-brand-500 mb-4"
              aria-hidden="true"
            />
            <div
              className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 ring-1 ring-red-200/70"
              aria-hidden="true"
            >
              <X className="h-8 w-8 text-red-600" strokeWidth={2.5} />
            </div>
            <h1 className="text-2xl font-bold font-display text-red-700 tracking-tight mb-3">
              {tCommon('error')}
            </h1>
            <p className="text-brand-800 leading-relaxed mb-6">
              {t('resetPasswordInvalidLink')}
            </p>
            <a
              href="/auth/forgot-password"
              className="inline-block font-semibold text-brand-600 hover:text-brand-700 hover:underline"
            >
              {t('forgotPassword')}
            </a>
          </div>
        </div>
      </main>
    )
  }

  // Success state: the API confirmed the password rotation. Navigation to
  // /auth/sign-in is scheduled by the effect above.
  if (resetMutation.isSuccess) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-gradient-to-bl from-brand-50/60 via-white to-brand-100/30 px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-xl border border-brand-100 p-8 sm:p-10 text-center">
            <div
              className="inline-block h-1 w-12 rounded-full bg-brand-500 mb-4"
              aria-hidden="true"
            />
            <div
              className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 ring-1 ring-emerald-200/70"
              aria-hidden="true"
            >
              <Check className="h-8 w-8 text-emerald-600" strokeWidth={2.5} />
            </div>
            <h1 className="text-2xl font-bold font-display text-emerald-700 tracking-tight mb-3">
              {t('resetPasswordSuccess')}
            </h1>
            <p className="text-brand-800 leading-relaxed">
              {tCommon('success')}
            </p>
          </div>
        </div>
      </main>
    )
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-bl from-brand-50/60 via-white to-brand-100/30 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-xl border border-brand-100 p-8 sm:p-10">
          <div className="mb-8 text-center">
            <div
              className="inline-block h-1 w-12 rounded-full bg-brand-500 mb-4"
              aria-hidden="true"
            />
            <h1 className="text-3xl font-bold font-display text-brand-900 tracking-tight mb-3">
              {t('resetPasswordTitle')}
            </h1>
            <p className="text-brand-800 text-sm leading-relaxed">
              {t('resetPasswordDescription')}
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              void form.handleSubmit()
            }}
            noValidate
          >
            <div className="space-y-5">
              <form.Field
                name="newPassword"
                validators={{
                  onChange: ({ value }) => {
                    if (!value) return 'required'
                    if (value.length < MIN_PASSWORD_LENGTH) return 'passwordTooShort'
                    return undefined
                  },
                }}
              >
                {(field) => {
                  const firstError = field.state.meta.errors[0]
                  const showError =
                    field.state.meta.isTouched && typeof firstError === 'string'
                  return (
                    <div>
                      <label
                        htmlFor={field.name}
                        className="block text-sm font-semibold text-brand-900 mb-1.5"
                      >
                        {t('newPassword')}
                      </label>
                      <Input
                        id={field.name}
                        type="password"
                        name={field.name}
                        autoComplete="new-password"
                        dir="ltr"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        aria-invalid={showError || undefined}
                        aria-describedby={showError ? `${field.name}-error` : undefined}
                      />
                      {showError ? (
                        <p
                          id={`${field.name}-error`}
                          className="text-xs text-red-600 mt-1.5"
                          role="alert"
                        >
                          {tValidation(firstError as ValidationKey)}
                        </p>
                      ) : null}
                    </div>
                  )
                }}
              </form.Field>

              <form.Field
                name="confirmPassword"
                validators={{
                  onChangeListenTo: ['newPassword'],
                  onChange: ({ value, fieldApi }) => {
                    if (!value) return 'required'
                    const passwordValue = fieldApi.form.getFieldValue('newPassword')
                    if (value !== passwordValue) return 'passwordMismatch'
                    return undefined
                  },
                }}
              >
                {(field) => {
                  const firstError = field.state.meta.errors[0]
                  const showError =
                    field.state.meta.isTouched && typeof firstError === 'string'
                  return (
                    <div>
                      <label
                        htmlFor={field.name}
                        className="block text-sm font-semibold text-brand-900 mb-1.5"
                      >
                        {t('confirmPassword')}
                      </label>
                      <Input
                        id={field.name}
                        type="password"
                        name={field.name}
                        autoComplete="new-password"
                        dir="ltr"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        aria-invalid={showError || undefined}
                        aria-describedby={showError ? `${field.name}-error` : undefined}
                      />
                      {showError ? (
                        <p
                          id={`${field.name}-error`}
                          className="text-xs text-red-600 mt-1.5"
                          role="alert"
                        >
                          {tValidation(firstError as ValidationKey)}
                        </p>
                      ) : null}
                    </div>
                  )
                }}
              </form.Field>

              {resetMutation.isError ? (
                <div
                  className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2"
                  role="alert"
                >
                  {t('resetPasswordInvalidLink')}
                </div>
              ) : null}

              <Button
                type="submit"
                size="lg"
                disabled={resetMutation.isPending}
                className="w-full"
              >
                {resetMutation.isPending ? tCommon('loading') : t('resetPasswordSubmit')}
              </Button>
            </div>
          </form>

          <p className="mt-6 text-center text-sm text-brand-800">
            <a
              href="/auth/sign-in"
              className="font-semibold text-brand-600 hover:text-brand-700 hover:underline"
            >
              {t('backToSignIn')}
            </a>
          </p>
        </div>
      </div>
    </main>
  )
}
